import React, { useContext, useState, useEffect, useRef } from "react";
import "../css/FilterComponent.css";
import DatePickerWithIcon from "../../../components/elements/DatePickerWithIcon";
import { StorageContext } from "../../../services/StorageProvider";
import ROUTE_PATHS from "../../../utils/routePaths";
import { useNavigate } from "react-router-dom";
import { renderAirportCode } from "../../../utils/Utils";
import SearchAirportDialog from "../../../components/elements/SearchAirportDialog";
import { TRIP_REQUEST_FILTERS_KEY } from "../../../utils/searchStorage";
import dayjs from "dayjs";

const FilterComponent = ({
  onFilterApplied,
  onFilterCleared,
  resetFilters,
  selectedTab,
  onTabChange,
  onPageReset,
}) => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    selectedArrivalAirport: "",
    selectedDepartureAirport: "",
    selectedDateFrom: null,
    registrationNumber: "",
  });
  const [savedFilters, setSavedFilters] = useState(null);
  const hasLoadedSavedFiltersRef = useRef(false);
  const hasMountedRef = useRef(false);
  const { airportData } = useContext(StorageContext);
  const [selectedArrivalAirport, setSelectedArrivalAirport] = useState(null);
  const [selectedDepartureAirport, setSelectedDepartureAirport] =
    useState(null);
  const [showSearchArrivalAirportDialog, setShowSearchArrivalAirportDialog] =
    useState(false);
  const [showSearchDepartureAirportDialog, setShowSearchDepartureAirportDialog] =
    useState(false);

  useEffect(() => {
    if (resetFilters) {
      setFilters({
        selectedArrivalAirport: "",
        selectedDepartureAirport: "",
        selectedDateFrom: null,
        registrationNumber: "",
      });
      setSelectedArrivalAirport(null);
      setSelectedDepartureAirport(null);
      localStorage.removeItem(TRIP_REQUEST_FILTERS_KEY);
    }
  }, [resetFilters]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    setFilters({
      selectedArrivalAirport: "",
      selectedDepartureAirport: "",
      selectedDateFrom: null,
      registrationNumber: "",
    });
    setSelectedArrivalAirport(null);
    setSelectedDepartureAirport(null);
    localStorage.removeItem(TRIP_REQUEST_FILTERS_KEY);
  }, [selectedTab]);

  useEffect(() => {
    if (hasLoadedSavedFiltersRef.current) {
      return;
    }
    hasLoadedSavedFiltersRef.current = true;
    const stored = localStorage.getItem(TRIP_REQUEST_FILTERS_KEY);
    if (stored) {
      try {
        setSavedFilters(JSON.parse(stored));
      } catch (error) {
        localStorage.removeItem(TRIP_REQUEST_FILTERS_KEY);
      }
    }
  }, []);

  useEffect(() => {
    if (!savedFilters) {
      return;
    }

    const restoredFilters = {
      ...savedFilters,
      selectedDateFrom: savedFilters.selectedDateFrom
        ? dayjs(savedFilters.selectedDateFrom)
        : null,
      registrationNumber:
        savedFilters.registrationNumber?.toUpperCase() || "",
    };

    setFilters(restoredFilters);
    const arrivalAirport = (airportData || []).find(
      (airport) => airport.id === restoredFilters.selectedArrivalAirport
    );
    const departureAirport = (airportData || []).find(
      (airport) => airport.id === restoredFilters.selectedDepartureAirport
    );
    setSelectedArrivalAirport(arrivalAirport || null);
    setSelectedDepartureAirport(departureAirport || null);
    onPageReset();
    if (onFilterApplied) {
      onFilterApplied(restoredFilters);
    }
    setSavedFilters(null);
  }, [savedFilters, airportData, onFilterApplied, onPageReset]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    onPageReset();
  };

  const handleArrivalAirportChange = (event) => {
    const selectedId = event.target.value;
    const selectedAirport = airportData.find(
      (airport) => airport.id === selectedId
    );
    handleFilterChange("selectedArrivalAirport", selectedId);
    setSelectedArrivalAirport(selectedAirport);
  };

  const handleDepartureAirportChange = (event) => {
    const selectedId = event.target.value;
    const selectedDepartureAirport = airportData.find(
      (airport) => airport.id === selectedId
    );
    handleFilterChange("selectedDepartureAirport", selectedId);
    setSelectedDepartureAirport(selectedDepartureAirport);
  };

  const handleSearchArrivalAirportChange = (airport) => {
    handleFilterChange("selectedArrivalAirport", airport.id);
    setSelectedArrivalAirport(airport);
  };

  const handleSearchDepartureAirportChange = (airport) => {
    handleFilterChange("selectedDepartureAirport", airport.id);
    setSelectedDepartureAirport(airport);
  };

  const handleTripClick = () => {
    navigate(ROUTE_PATHS.HISTORIC_TRIPS_PAGE);
  };
  const handleUnregisteredAircraftTripClick = () => {
    navigate(ROUTE_PATHS.UNREGISTERED_AIRCRAFT_TRIPS_PAGE);
  };

  const handleClearAll = () => {
    setFilters({
      selectedArrivalAirport: "",
      selectedDepartureAirport: "",
      selectedDateFrom: null,
      registrationNumber: "",
    });
    setSelectedArrivalAirport(null);
    setSelectedDepartureAirport(null);
    onPageReset();
    localStorage.removeItem(TRIP_REQUEST_FILTERS_KEY);
    if (onFilterCleared) {
      onFilterCleared();
    }
  };

  const handleApply = () => {
    if (onFilterApplied) {
      onPageReset();
      onFilterApplied(filters);
      const storedFilters = {
        ...filters,
        selectedDateFrom: filters.selectedDateFrom
          ? filters.selectedDateFrom.toISOString()
          : null,
      };
      localStorage.setItem(
        TRIP_REQUEST_FILTERS_KEY,
        JSON.stringify(storedFilters)
      );
    }
  };

  const isAnyFilterSelected = () => {
    return (
      filters.selectedArrivalAirport ||
      filters.selectedDepartureAirport ||
      filters.selectedDateFrom ||
      (filters.registrationNumber &&
        filters.registrationNumber.trim() !== "")
    );
  };

  const handleAircraftRegistrationNumberChange = (e) => {
    const value = e.target.value.toUpperCase();
    // When user manually clears ARN, reset list like Clear All
    if (!value || value.trim() === "") {
      handleClearAll();
      return;
    }
    handleFilterChange("registrationNumber", value);
  };

  const showButtonsRow = isAnyFilterSelected();

  const handleTabChange = (tabValue) => {
    onTabChange(tabValue);
  };

  if (!selectedTab) {
    return null;
  }

  return (
    <div>
      <div className="trip-request-filter-bar">
        <div className="trip-request-filter-field">
          <div className="trip-request-filter-label">Select Arrival Airport</div>
          <div
            className="trip-request-filter-select-airport"
            onClick={() => setShowSearchArrivalAirportDialog(true)}
          >
            {selectedArrivalAirport ? (
              <p>{selectedArrivalAirport.name} - {renderAirportCode(selectedArrivalAirport.iataCode, selectedArrivalAirport.icaoCode)}</p>
            ) : (
              <span className="placeholder-text">Search</span>
            )}
          </div>
        </div>

        <div className="trip-request-filter-field">
          <div className="trip-request-filter-label">Select Departure Airport</div>
          <div
            className="trip-request-filter-select-airport"
            onClick={() => setShowSearchDepartureAirportDialog(true)}
          >
            {selectedDepartureAirport ? (
              <p>{selectedDepartureAirport.name} - {renderAirportCode(selectedDepartureAirport.iataCode, selectedDepartureAirport.icaoCode)}</p>
            ) : (
              <span className="placeholder-text">Search</span>
            )}
          </div>
        </div>

        <DatePickerWithIcon
          label="Departure Date"
          value={filters?.selectedDateFrom}
          onChange={(newValue) =>
            handleFilterChange("selectedDateFrom", newValue)
          }
        />
        <div className="trip-request-filter-field">
          <div className="trip-request-filter-label">ARN</div>
          <input
            type="text"
            className="trip-request-filter-email-input"
            placeholder="Enter ARN"
            value={filters.registrationNumber || ""}
            onChange={handleAircraftRegistrationNumberChange}
          />
        </div>

        {showButtonsRow && (
          <div className="trip-request-filter-buttons">
            <div
              className="trip-request-clear-all-button"
              onClick={handleClearAll}
            >
              Clear All
            </div>
            <button className="trip-request-apply-button" onClick={handleApply}>
              Apply
            </button>
          </div>
        )}
        <div className="trip-request-unregistered-aircraft-trips">
          <p onClick={handleUnregisteredAircraftTripClick}>Unregistered Aircraft Trips</p>
        </div>
        <div className="trip-request-historic-trips">
          <p onClick={handleTripClick}>Historic Trips</p>
        </div>
      </div>
      <div className="trip-request-tabs">
        <button
          className={`trip-request-tab-button ${selectedTab === "ALL" ? "active" : ""
            }`}
          onClick={() => handleTabChange("ALL")}
        >
          All
        </button>
        <button
          className={`trip-request-tab-button ${selectedTab === "SPECIAL_ONBOARDING" ? "active" : ""
            }`}
          onClick={() => handleTabChange("SPECIAL_ONBOARDING")}
        >
          Special Onboarding
        </button>
        <button
          className={`trip-request-tab-button ${selectedTab === "AFTER_HOURS" ? "active" : ""
            }`}
          onClick={() => handleTabChange("AFTER_HOURS")}
        >
          After Hours
        </button>
        <button
          className={`trip-request-tab-button ${selectedTab === "AUTO_GENERATED" ? "active" : ""
            }`}
          onClick={() => handleTabChange("AUTO_GENERATED")}
        >
          Auto-Generated
        </button>
        <button
          className={`trip-request-tab-button ${selectedTab === "MANUALLY_CREATED" ? "active" : ""
            }`}
          onClick={() => handleTabChange("MANUALLY_CREATED")}
        >
          Manually Created
        </button>
      </div>
      <SearchAirportDialog
        open={showSearchArrivalAirportDialog}
        onClose={() => setShowSearchArrivalAirportDialog(false)}
        airportData={airportData}
        onSelectAirport={handleSearchArrivalAirportChange}
      />
      <SearchAirportDialog
        open={showSearchDepartureAirportDialog}
        onClose={() => setShowSearchDepartureAirportDialog(false)}
        airportData={airportData}
        onSelectAirport={handleSearchDepartureAirportChange}
      />
    </div>
  );
};

export default FilterComponent;
